import React, { useMemo, useState } from 'react';
import { FileDownIcon, PlusIcon, SearchIcon } from 'lucide-react';
import type { CustodyCase } from '../../types/case';
import type { AdminPageProps } from '../../types/navigation';
import { useStore } from '../../contexts/StoreContext';
import { PageHeader } from '../../components/admin/PageHeader';
import { ExportPreview } from '../../components/admin/ExportPreview';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { OfficePaymentModal } from '../../components/admin/payments/OfficePaymentModal';
import { PaymentDetailModal } from '../../components/admin/payments/PaymentDetailModal';
import { PAYMENT_LABEL, isPaid, paymentSourceLabel, type Labeled } from '../../utils/caseStatus';
import { can, DENIED } from '../../utils/permissions';
import { bn, fmtDate, fmtDateTime, taka } from '../../utils/bn';
type Tab = 'reconcile' | 'outstanding' | 'ledger' | 'summary';
const reconLabel = (c: CustodyCase): Labeled => c.paymentStatus === 'reconciled' ? {
  label: 'সমন্বিত',
  tone: 'green'
} : c.paymentStatus === 'paid' ? {
  label: 'সমন্বয় অপেক্ষমাণ',
  tone: 'warning'
} : c.paymentStatus === 'pending_verify' ? {
  label: 'দাবি যাচাই অপেক্ষমাণ',
  tone: 'danger'
} : {
  label: 'প্রযোজ্য নয়',
  tone: 'gray'
};
export function Payments({
  role,
  focus
}: AdminPageProps) {
  const {
    cases
  } = useStore();
  const live = cases.filter(c => c.stage !== 'registered');
  const groups = useMemo(() => ({
    reconcile: live.filter(c => c.paymentStatus === 'paid' || c.paymentStatus === 'pending_verify'),
    outstanding: live.filter(c => c.stage !== 'released' && ['unpaid', 'failed', 'correction', 'processing'].includes(c.paymentStatus)),
    ledger: live.filter(isPaid).sort((a, b) => (b.payment.paidAt ?? '').localeCompare(a.payment.paidAt ?? ''))
  }), [live]);
  const [tab, setTab] = useState<Tab>(focus && groups.outstanding.some(c => c.pcr === focus) ? 'outstanding' : 'reconcile');
  const [q, setQ] = useState('');
  const [source, setSource] = useState('');
  const [detail, setDetail] = useState<string | null>(focus && cases.some(c => c.pcr === focus) ? focus : null);
  const [office, setOffice] = useState<{
    open: boolean;
    pcr?: string;
  }>({
    open: false
  });
  const [exportOpen, setExportOpen] = useState(false);
  const collected = groups.ledger.reduce((s, c) => s + c.fine + c.extraCharge, 0);
  const awaiting = live.filter(c => c.paymentStatus === 'paid');
  const claims = live.filter(c => c.paymentStatus === 'pending_verify');
  const due = groups.outstanding.reduce((s, c) => s + c.fine + c.extraCharge, 0);
  const rows = (tab === 'summary' ? [] : groups[tab]).filter(c => !q || [c.pcr, c.regNo, c.payment.txnId ?? '', c.payment.challan ?? ''].some(v => v.toLowerCase().includes(q.toLowerCase()))).filter(c => !source || (source === 'claim' ? c.paymentStatus === 'pending_verify' : c.payment.source === source && c.paymentStatus !== 'pending_verify'));
  const byChannel = Object.entries(groups.ledger.reduce<Record<string, {
    n: number;
    amt: number;
  }>>((m, c) => {
    const k = c.payment.channel ?? 'অজ্ঞাত';
    m[k] = {
      n: (m[k]?.n ?? 0) + 1,
      amt: (m[k]?.amt ?? 0) + c.fine + c.extraCharge
    };
    return m;
  }, {})).sort((a, b) => b[1].amt - a[1].amt);
  const onlineAmt = groups.ledger.filter(c => c.payment.source === 'online').reduce((s, c) => s + c.fine + c.extraCharge, 0);
  const tabs: {
    id: Tab;
    label: string;
    n?: number;
  }[] = [{
    id: 'reconcile',
    label: 'সমন্বয় ও যাচাই অপেক্ষমাণ',
    n: groups.reconcile.length
  }, {
    id: 'outstanding',
    label: 'বকেয়া / ব্যর্থ',
    n: groups.outstanding.length
  }, {
    id: 'ledger',
    label: 'লেনদেন লেজার',
    n: groups.ledger.length
  }, {
    id: 'summary',
    label: 'আদায় সারসংক্ষেপ'
  }];
  const selected = cases.find(c => c.pcr === detail) ?? null;
  return <>
      <PageHeader title="জরিমানা ও পেমেন্ট" crumbs={['জরিমানা ও পেমেন্ট']} description="অনলাইন লেনদেন সমন্বয়, অফিসে আদায় লিপিবদ্ধকরণ ও লেনদেন লেজার — সকল পরিমাণ নমুনা" actions={<>
          <button className="btn btn-outline" onClick={() => setExportOpen(true)} data-id="element-1283" data-mp="2ski4ifis3hhs"><FileDownIcon className="h-4 w-4" data-id="element-1284" data-mp="2xhqnujfnxrkm" /> লেজার রপ্তানি</button>
          <button className="btn btn-primary" disabled={!can(role, 'office_payment')} title={can(role, 'office_payment') ? undefined : DENIED} onClick={() => setOffice({
        open: true
      })} data-id="element-1285" data-mp="2xfxm9ijwpxjh"><PlusIcon className="h-4 w-4" data-id="element-1286" data-mp="379ee4h0hh2ki" /> অফিস পেমেন্ট রেকর্ড</button>
        </>} data-id="element-1282" data-mp="2jtyqeyyntziw" />

      <section className="card mb-5 grid overflow-hidden md:grid-cols-[1.4fr_1fr_1fr_1fr] md:divide-x divide-line" data-id="element-1287" data-mp="3b8vlxh3dubsu">
        <div className="bg-navy p-5 text-white" data-id="element-1288" data-mp="3g6x47zszi739">
          <p className="text-sm text-white/75" data-id="element-1289" data-mp="3l7b8d6rgsww0">মোট আদায় (ডেমো কেস)</p>
          <p className="mt-1 text-3xl font-bold" data-id="element-1290" data-mp="3q6xopp2lat91">{taka(collected)}</p>
          <p className="mt-1 text-xs text-white/70" data-id="element-1291" data-mp="3q664tbicujyl">অনলাইন {taka(onlineAmt)} · অফিস {taka(collected - onlineAmt)}</p>
        </div>
        {[{
        label: 'সমন্বয় অপেক্ষমাণ',
        v: bn(awaiting.length),
        sub: taka(awaiting.reduce((s, c) => s + c.fine + c.extraCharge, 0)),
        go: 'reconcile' as Tab
      }, {
        label: 'অযাচাইকৃত দাবি',
        v: bn(claims.length),
        sub: 'পরিশোধ হিসেবে গণ্য নয়',
        go: 'reconcile' as Tab,
        danger: true
      }, {
        label: 'বকেয়া / ব্যর্থ',
        v: bn(groups.outstanding.length),
        sub: taka(due),
        go: 'outstanding' as Tab
      }].map(k => <button key={k.label} onClick={() => setTab(k.go)} className="p-5 text-left transition-colors duration-150 hover:bg-canvas" data-id="element-1292" data-mp="3vcuh2dltuqid">
            <p className="text-sm text-ink-muted" data-id="element-1293" data-mp="vuk0e99stxx9">{k.label}</p>
            <p className={`mt-1 text-2xl font-bold ${k.danger && claims.length ? 'text-danger' : 'text-ink'}`} data-id="element-1294" data-mp="rw475nn4jtbe">{k.v}</p>
            <p className="text-xs text-ink-muted" data-id="element-1295" data-mp="muete08ip1jm">{k.sub}</p>
          </button>)}
      </section>

      <div className="mb-4 flex flex-wrap items-center gap-2" data-id="element-1296" data-mp="hwn1353yndew">
        <div className="flex flex-wrap gap-1 rounded-lg border border-line bg-white p-1" role="tablist" data-id="element-1297" data-mp="37mgv94tu5nw8">
          {tabs.map(t => <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)} className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors duration-150 ${tab === t.id ? 'bg-navy text-white' : 'text-ink-muted hover:text-ink'}`} data-id="element-1298" data-mp="32lsxm0b3q8t1">
              {t.label}{t.n !== undefined && <span className="ml-1 opacity-75" data-id="element-1299" data-mp="2v5pazfnr4zwj">{bn(t.n)}</span>}
            </button>)}
        </div>
        {tab !== 'summary' && <>
            <select className="input h-10 w-auto" value={source} onChange={e => setSource(e.target.value)} aria-label="উৎস" data-id="element-1300" data-mp="2lcqmdxs999cx">
              <option value="" data-id="element-1301" data-mp="2q5t8xlym7ijm">সকল উৎস</option><option value="online" data-id="element-1302" data-mp="2cd3nbin00fn1">অনলাইন গেটওয়ে</option><option value="office" data-id="element-1303" data-mp="2ceemt69ev18c">অফিসে লিপিবদ্ধ</option><option value="claim" data-id="element-1304" data-mp="2cbaepxstrlce">অযাচাইকৃত দাবি</option>
            </select>
            <div className="relative min-w-[220px] flex-1" data-id="element-1305" data-mp="2geynukx3uozr">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-soft" data-id="element-1306" data-mp="22fyw34eaptzd" />
              <input className="input h-10 pl-9" placeholder="PCR, যানবাহন, লেনদেন বা চালান নম্বর" value={q} onChange={e => setQ(e.target.value)} aria-label="অনুসন্ধান" data-id="element-1307" data-mp="27cz3bimvt2s2" />
            </div>
          </>}
      </div>

      {tab !== 'summary' ? <div className="card overflow-x-auto scroll-thin" data-id="element-1308" data-mp="1xqen1ekx9yfl">
          <table className="w-full" data-id="element-1309" data-mp="1xrzhl478lhdg">
            <thead className="bg-canvas" data-id="element-1310" data-mp="1srbgfppank5i"><tr data-id="element-1311" data-mp="1sqs72xdpm433">{['PCR নম্বর', 'নির্ধারিত জরিমানা', 'অতিরিক্ত চার্জ', 'পেমেন্ট চ্যানেল', 'লেনদেন / চালান নম্বর', 'আদায়ের তারিখ', 'পেমেন্ট অবস্থা', 'সমন্বয় অবস্থা', 'উৎস', 'কার্যক্রম'].map(h => <th key={h} className="th" data-id="element-1312" data-mp="21s9l8a7k3vp5">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-line" data-id="element-1313" data-mp="21rq5n05dpjea">
              {rows.map(c => <tr key={c.pcr} className="cursor-pointer hover:bg-navy-50/50" onClick={() => setDetail(c.pcr)} data-id="element-1314" data-mp="2ggcvvwwjnvka">
                  <td className="td" data-id="element-1315" data-mp="2bjw5sx5sb74k"><span className="font-mono font-semibold text-navy" data-id="element-1316" data-mp="2phkez7qhopts">{c.pcr}</span><br data-id="element-1317" data-mp="2pi3vql4el868" /><span className="text-xs text-ink-muted" data-id="element-1318" data-mp="2phu8346tzd3p">{c.regNo}</span></td>
                  <td className="td" data-id="element-1319" data-mp="2lha8zv2p5zex">{taka(c.fine)}</td>
                  <td className="td" data-id="element-1320" data-mp="3rv702lz1yuuy">{taka(c.extraCharge)}</td>
                  <td className="td" data-id="element-1321" data-mp="3ruowf4a29ed0">{c.payment.channel ?? '—'}</td>
                  <td className="td font-mono text-xs" data-id="element-1322" data-mp="nkrqg1e247r">{c.payment.txnId ?? c.payment.challan ?? '—'}</td>
                  <td className="td" data-id="element-1323" data-mp="5p1pw3shnckn">{fmtDate(c.payment.paidAt)}</td>
                  <td className="td" data-id="element-1324" data-mp="9ptzwz96plhz"><StatusBadge size="sm" {...PAYMENT_LABEL[c.paymentStatus]} data-id="element-1325" data-mp="9pljixdury21" /></td>
                  <td className="td" data-id="element-1326" data-mp="394z0gdukamji"><StatusBadge size="sm" {...reconLabel(c)} data-id="element-1327" data-mp="395i9t665c2lx" /></td>
                  <td className="td" data-id="element-1328" data-mp="3e3ac8u4f374d"><StatusBadge size="sm" {...paymentSourceLabel(c)} data-id="element-1329" data-mp="3e2qsr8z4cao8" /></td>
                  <td className="td" onClick={e => e.stopPropagation()} data-id="element-1330" data-mp="3i55cdvam3egk">
                    {c.paymentStatus === 'paid' ? <button className="btn btn-green btn-sm" onClick={() => setDetail(c.pcr)} data-id="element-1331" data-mp="3mvlptfefn02x">যাচাই ও সমন্বয়</button> : c.paymentStatus === 'pending_verify' ? <button className="btn btn-danger btn-sm" onClick={() => setDetail(c.pcr)} data-id="element-1332" data-mp="2qdpgfan60fdo">দাবি পর্যালোচনা</button> : !isPaid(c) ? <button className="btn btn-outline btn-sm" disabled={!can(role, 'office_payment')} onClick={() => setOffice({
                open: true,
                pcr: c.pcr
              })} data-id="element-1333" data-mp="2mvrzduox3g0i">অফিস পেমেন্ট</button> : <button className="btn btn-ghost btn-sm" onClick={() => setDetail(c.pcr)} data-id="element-1334" data-mp="2hv4apfo5vb9g">বিস্তারিত</button>}
                  </td>
                </tr>)}
              {rows.length === 0 && <tr data-id="element-1335" data-mp="28yd8elj0n06f"><td colSpan={10} className="py-12 text-center text-sm text-ink-muted" data-id="element-1336" data-mp="28ylhe2c0plcc">এই তালিকায় কোনো লেনদেন নেই।</td></tr>}
            </tbody>
          </table>
        </div> : <div className="grid gap-5 lg:grid-cols-[1.3fr_1fr]" data-id="element-1337" data-mp="1z54lmssblw61">
          <section className="card p-5" data-id="element-1338" data-mp="1u8eamjvdhqrh">
            <h2 className="mb-3 text-base font-bold text-ink" data-id="element-1339" data-mp="1u59z11fkvsqs">চ্যানেলভিত্তিক আদায়</h2>
            <table className="w-full" data-id="element-1340" data-mp="1q7m07narlftm">
              <thead data-id="element-1341" data-mp="1l6eq3hl5g4js"><tr data-id="element-1342" data-mp="1l77pfxhs3rrw"><th className="th pl-0" data-id="element-1343" data-mp="1l77nvt24rjps">চ্যানেল</th><th className="th" data-id="element-1344" data-mp="1l64zpsw6zvdk">লেনদেন</th><th className="th text-right" data-id="element-1345" data-mp="1l8itlyf4z1jj">পরিমাণ</th></tr></thead>
              <tbody className="divide-y divide-line" data-id="element-1346" data-mp="1g83htbai623o">
                {byChannel.map(([k, v]) => <tr key={k} data-id="element-1347" data-mp="1qhrjpqk26631">
                    <td className="td pl-0" data-id="element-1348" data-mp="1qgy8ode2guf3">
                      <p className="font-semibold" data-id="element-1349" data-mp="1mgo4955xac2c">{k}</p>
                      <div className="mt-1 h-1.5 w-full max-w-[220px] rounded-full bg-canvas" data-id="element-1350" data-mp="209498bidwk5w"><div className="h-1.5 rounded-full bg-navy" style={{
                    width: `${Math.round(v.amt / Math.max(collected, 1) * 100)}%`
                  }} data-id="element-1351" data-mp="208ccgxd1wge1" /></div>
                    </td>
                    <td className="td" data-id="element-1352" data-mp="17l83yier1jxi">{bn(v.n)}</td>
                    <td className="td text-right font-semibold" data-id="element-1353" data-mp="13iv0k0nh5y3w">{taka(v.amt)}</td>
                  </tr>)}
              </tbody>
            </table>
          </section>
          <section className="card p-5" data-id="element-1354" data-mp="1hgjl17dmr38f">
            <h2 className="mb-3 text-base font-bold text-ink" data-id="element-1355" data-mp="1cj04g0tgmedb">সাম্প্রতিক সমন্বয়</h2>
            <ul className="space-y-3" data-id="element-1356" data-mp="2sxjcy1w2fg1x">
              {groups.ledger.filter(c => c.payment.reconciledAt).sort((a, b) => (b.payment.reconciledAt ?? '').localeCompare(a.payment.reconciledAt ?? '')).slice(0, 6).map(c => <li key={c.pcr} className="flex items-start justify-between gap-3 text-sm" data-id="element-1357" data-mp="9pw258jhw1ud">
                  <div data-id="element-1358" data-mp="eqaakrp0ddsx"><p className="font-mono font-semibold text-navy" data-id="element-1359" data-mp="er39x7ln1111">{c.pcr}</p><p className="text-xs text-ink-muted" data-id="element-1360" data-mp="q8ysh77glyo">{c.payment.reconciledBy} · {fmtDateTime(c.payment.reconciledAt)}</p></div>
                  <span className="font-semibold" data-id="element-1361" data-mp="5ok90t1ewyhf">{taka(c.fine + c.extraCharge)}</span>
                </li>)}
            </ul>
          </section>
        </div>}

      <PaymentDetailModal c={selected} role={role} onClose={() => setDetail(null)} onRecordOffice={p => {
      setDetail(null);
      setOffice({
        open: true,
        pcr: p
      });
    }} data-id="element-1362" data-mp="xg9wx6tgsesn" />
      <OfficePaymentModal open={office.open} role={role} initialPcr={office.pcr} onClose={() => setOffice({
      open: false
    })} data-id="element-1363" data-mp="jov2m3u3wr6a" />
      <ExportPreview open={exportOpen} onClose={() => setExportOpen(false)} title="জরিমানা আদায় লেনদেন লেজার" columns={['PCR', 'যানবাহন', 'জরিমানা', 'অতিরিক্ত', 'চ্যানেল', 'লেনদেন/চালান', 'তারিখ', 'অবস্থা', 'উৎস']} rows={groups.ledger.map(c => [c.pcr, c.regNo, taka(c.fine), taka(c.extraCharge), c.payment.channel ?? '', c.payment.txnId ?? c.payment.challan ?? '', fmtDate(c.payment.paidAt), PAYMENT_LABEL[c.paymentStatus].label, paymentSourceLabel(c).label])} data-id="element-1364" data-mp="1cdski5ueolyi" />
    </>;
}